export const GIVES_RELAY = 'wss://relay.nostr.gives' as const;

export const PUBLIC_FALLBACK_RELAYS = [
  'wss://relay.damus.io',
  'wss://nos.lol',
  'wss://relay.nostr.band',
] as const;

export const DEFAULT_RELAYS = [GIVES_RELAY, ...PUBLIC_FALLBACK_RELAYS] as const;

// Read-relay set for one-shot NIP-01 kind-0 (profile metadata) fan-out.
// Used by packages/frontend/src/lib/profile.ts to resolve the signed-in
// user's display name + picture so the header avatar shows their PFP
// instead of a 2-character pubkey stub, and to resolve every leaderboard
// row's pfp + display name (PM 2026-06-16 brief). Per nostr-surf
// relay-fetcher.ts pattern: open a WebSocket per relay, send REQ,
// collect until EOSE, close. Fan-out runs in parallel; newest event
// (by created_at) wins across all relays that responded in time.
//
// Coverage is the priority here - leaderboard pfp resolution needs the
// widest community-indexed set so we don't 404 every other row:
//   - GIVES_RELAY: our own write target
//   - relay.damus.io: iOS Damus default
//   - relay.snort.social: Snort web default
//   - relay.primal.net: Primal default (timed out in dev probe but worth
//     trying, succeeds intermittently)
//   - nos.lol: large community relay, broad kind 0 mirror
//   - relay.nostr.band: nostr.band search index (kind 0 indexer of last
//     resort - if anyone has ever published a profile event, it lives here)
export const READ_PROFILE_RELAYS = [
  GIVES_RELAY,
  'wss://relay.damus.io',
  'wss://relay.snort.social',
  'wss://relay.primal.net',
  'wss://nos.lol',
  'wss://relay.nostr.band',
] as const;

// Relay set advertised in nostrconnect:// URIs for NIP-46 bunker pairing.
//
// We include `relay.powr.build` alongside our own relay because clave.casa's
// signer reaches it via the Clave relay-proxy primary subscription, which
// triggers an APNs wake of the iOS Notification Service Extension when the
// app is backgrounded. Without a relay clave's proxy is already subscribed
// to, the connect-response is published to a relay clave isn't actively
// watching on the device — surfacing as a "connection failed: no relay
// specified" UX error on the signer side.
//
// nostr-tools `BunkerSigner.fromURI` subscribes to every `relay=` param in
// the URI, so the dial-back lands on whichever relay both sides can reach.
// See Vault/Operations/Clave Compat.md and clave's docs/nip46-compatibility.md.
export const BUNKER_RELAYS = [
  GIVES_RELAY,
  'wss://relay.powr.build',
] as const;

// Launch game suite for nostr.gives.
// LeWord (5-letter Wordle clone) was deliberately excluded at launch
// (PM 2026-06-09) on the otherstuff.ai/word5 overlap rationale, then
// reversed 2026-06-18 (LeWord is the platform's daily Wordle slot;
// Hard Mode + nostr-native share differentiate it). Phase 31 ships it.
// Quordle remains out of scope.
export const GAME_IDS = [
  'connections',
  'worldle',
  'flagle',
  '2048',
  'echo',
  'acrostic',
  'letter-boxed',
  'word-ladder',
  'foldle',
  'cross-number',
  'akari',
  'kakuro',
  'leword',
  'nerdle',
  'spelling-bee',
  'chess-puzzle',
  'heardle',
  'minesweeper',
  'connect4',
  'nonogram',
  'longword',
  'mathdoku',
  'compound',
  'slitherlink',
  'battleships',
  // 'anagram-sprint' removed 2026-06-11: same UX as Spelling Bee with a
  // 90s timer slapped on top, and Bowz called it redundant. The 180-day
  // catalogue + worker route are kept on disk for the timer-chip UX
  // pattern extraction (see Spelling Bee chip header). Backlog: decide
  // whether to delete the catalogue+route entirely or keep them
  // dormant for a future Anagram Sprint v2 that genuinely differentiates.
] as const;

export type GameId = (typeof GAME_IDS)[number];

// Category map for the homepage tile grid (PM 2026-06-12).
// Drives the section headings ("Word Games", "Math Games", ...) and the
// per-category sub-grids in `packages/frontend/src/main.ts` -> renderHome().
// Mirrors the canonical category split documented in
// `Vault/Architecture/Scoring System.md` v2.
//
// `equate` is the worker-side id for the Nerdle-family game; we list both
// the worker id and the legacy `nerdle` id under Math so historical aliases
// keep bucketing correctly. The 'other' bucket is reserved for currently-
// unscored games (Anagram Sprint was removed 2026-06-11).
export type GameCategory = 'word' | 'math' | 'board' | 'puzzle' | 'other';

export const GAME_CATEGORIES: Record<GameId, GameCategory> = {
  // Word
  'spelling-bee': 'word',
  longword: 'word',
  leword: 'word',
  connections: 'word',
  compound: 'word',
  acrostic: 'word',
  'letter-boxed': 'word',
  'word-ladder': 'word',
  // Math
  nerdle: 'math',
  mathdoku: 'math',
  nonogram: 'math',
  'cross-number': 'math',
  kakuro: 'math',
  // Board
  battleships: 'board',
  minesweeper: 'board',
  connect4: 'board',
  // Puzzle
  'chess-puzzle': 'puzzle',
  slitherlink: 'puzzle',
  worldle: 'puzzle',
  flagle: 'puzzle',
  '2048': 'puzzle',
  heardle: 'puzzle',
  foldle: 'puzzle',
  akari: 'puzzle',
  // Other (memory + audio working-memory genre; Anagram Sprint disabled per Bowz 2026-06-11).
  echo: 'other',
};

export const GAME_CATEGORY_LABELS: Record<GameCategory, string> = {
  word: 'Word Games',
  math: 'Math Games',
  board: 'Board Games',
  puzzle: 'Puzzle Games',
  other: 'Other Games',
};

export const GAME_CATEGORY_ORDER: GameCategory[] = [
  'word',
  'math',
  'board',
  'puzzle',
  'other',
];
